import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download } from "lucide-react";

const ExportRow = ({ title, base }) => (
  <Card className="p-5 flex items-center justify-between gap-3">
    <div>
      <div className="font-display font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">Choose export format</div>
    </div>
    <div className="flex gap-2">
      {["csv", "xlsx", "pdf"].map((f) => (
        <Button key={f} asChild size="sm" variant="outline" className="gap-1" data-testid={`export-${title.toLowerCase().replace(/[^a-z]/g,'-')}-${f}`}>
          <a href={`${API}${base}?fmt=${f}`} download><Download className="w-3.5 h-3.5"/>{f.toUpperCase()}</a>
        </Button>
      ))}
    </div>
  </Card>
);

const Table = ({ rows, columns }) => (
  <Card className="overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            {columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {columns.map((c) => <td key={c.key} className="px-4 py-3">{r[c.key] ?? "—"}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="text-center py-8 text-slate-400">No data</td></tr>}
        </tbody>
      </table>
    </div>
  </Card>
);

export default function AdminReports() {
  const [emp, setEmp] = useState([]);
  const [lang, setLang] = useState([]);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/reports/employee-wise"),
      api.get("/admin/reports/language-wise"),
      api.get("/admin/reports/daily?days=30"),
    ]).then(([e, l, d]) => { setEmp(e.data); setLang(l.data); setDaily(d.data); });
  }, []);

  return (
    <Shell role="admin">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Reports</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Generate & Export</h1>
      </div>

      <div className="grid gap-3 mb-8">
        <ExportRow title="Employee-wise" base="/admin/reports/employee-wise" />
        <ExportRow title="Language-wise" base="/admin/reports/language-wise" />
        <ExportRow title="Daily Activity (30d)" base="/admin/reports/daily" />
      </div>

      <Tabs defaultValue="emp">
        <TabsList>
          <TabsTrigger value="emp" data-testid="tab-emp">Employee-wise</TabsTrigger>
          <TabsTrigger value="lang" data-testid="tab-lang">Language-wise</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
        </TabsList>
        <TabsContent value="emp" className="mt-4">
          <Table rows={emp} columns={[
            { key: "emp_code", label: "EMP Code" },
            { key: "name", label: "Name" },
            { key: "posters", label: "Posters" },
            { key: "last_generated", label: "Last Generated" },
          ]}/>
        </TabsContent>
        <TabsContent value="lang" className="mt-4">
          <Table rows={lang} columns={[
            { key: "language", label: "Language" }, { key: "posters", label: "Posters" },
          ]}/>
        </TabsContent>
        <TabsContent value="daily" className="mt-4">
          <Table rows={daily} columns={[
            { key: "date", label: "Date" }, { key: "posters", label: "Posters" },
          ]}/>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
